import { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axios";
import { AuthContext } from "../contexts/AuthContext";
import { dateOnlyFormat } from "../utils/dateFormats";
import "../styles/EventDetail.scss";

const EventDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  const [event, setEvent] = useState();
  const [loading, setLoading] = useState(!event);

  const MAX_TICKETS_PER_SECTION = 10;

  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // 🆕 Group pricing calculator
  const calcPrice = (section, qty) => {
    if (qty < 1 || !section) return { usedPrice: 0, total: 0, savings: 0 };

    const basePrice = parseFloat(section.price || 0);
    let usedPrice = basePrice;
    let savings = 0;

    if (section.group_prices && Array.isArray(section.group_prices)) {
      // Sort ascending min_quantity, find first applicable
      const applicable = section.group_prices
        .sort((a, b) => a.min_quantity - b.min_quantity)
        .find(gp => gp.min_quantity === qty);

      if (applicable) {
        usedPrice = parseFloat(applicable.group_price);
        savings = Math.round((basePrice - usedPrice) * qty * 100) / 100;
      }
    }

    const total = Math.round(usedPrice * qty * 100) / 100;

    return { usedPrice, total, savings };
  };

  // ✅ Fetch event (ID based + fallback)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const fetchEvent = async () => {
      if (event) return;

      if (!navigator.onLine) {
        toast("Offline. Please check connection.");
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // 🔥 Primary API (ID-based)
        const response = await Promise.race([
          axiosInstance.get(`customer/event/${id}`),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout")), 5000)
          ),
        ]);

        setEvent(response.data);
      } catch (error) {
        console.warn("Primary API failed, trying fallback...");

        // 🔁 Fallback (home API)
        try {
          const res = await axiosInstance.get("customer/home");
          const events = res.data.latest_events || [];

          const foundEvent = events.find(
            (e) => e.id == id
          );

          if (foundEvent) {
            setEvent(foundEvent);
          } else {
            toast("Event not found");
          }
        } catch {
          toast("Failed to load event");
        }
      } finally {
        setLoading(false);
      }
    };

    if (id && !event) {
      fetchEvent();
    }
  }, [id, event]);

  // ✅ Ticket helpers
  const getAvailableTickets = (section) => {
    return Math.max(
      0,
      section.released - (section.reserved + section.booking_count)
    );
  };

  const handleSectionClick = (section) => {
    const available = getAvailableTickets(section);

    if (section.released === 0) {
      alert("Booking not started yet");
      return;
    }

    if (available <= 0) {
      alert("All released seats are booked");
      return;
    }

    setSelectedSection(section);
    setQuantity(1);
    setShowQuantityModal(true);
  };

  const handleQuantitySubmit = async () => {
    if (!user || !user.isAuthenticated) {
      alert("Please log in to book tickets");
      setTimeout(() => navigate("/signin"), 1000);
      return;
    }

    if (quantity > MAX_TICKETS_PER_SECTION) {
      alert("Max 10 tickets allowed");
      return;
    }

    const available = getAvailableTickets(selectedSection);

    if (quantity > available) {
      alert("Not enough tickets available");
      return;
    }

    try {
      setIsProcessing(true);

      const priceInfo = calcPrice(selectedSection, quantity);
      const amount_to_pay = priceInfo.total;

      const response = await axiosInstance.post(
        "news_and_events/event-booking/",
        {
          event: event.id,
          sections: [{ section_id: selectedSection.id, quantity }],
          number_of_tickets: quantity,
          amount_to_pay,
          booked_by_email: user.name || "",
          is_reserved: true,
        }
      );

      const { client_secret, booking_id, service_charge } = response.data;

      navigate("/event-payment", {
        state: {
          event,
          selectedSection,
          quantity,
          clientSecret: client_secret,
          bookingId: booking_id,
          service_charge,
        },
      });
    } catch {
      alert("Payment initiation failed");
    } finally {
      setIsProcessing(false);
    }
  };

  // ✅ UI states
  if (loading) {
    return <div className="text-center py-5">Loading...</div>;
  }

  if (!event) {
    return <div className="text-center py-5">Event not found</div>;
  }

  const availableTickets = selectedSection
    ? Math.min(
        getAvailableTickets(selectedSection),
        MAX_TICKETS_PER_SECTION
      )
    : 0;

  return (
    <div className="event-detail">
      {/* Banner */}
      <div
        className="event-detail-banner"
        style={{ backgroundImage: `url(${event.thumbnail})` }}
      >
        <div className="overlay">
          <div className="container banner-content">
            <img
              src={event.thumbnail}
              alt={event.heading}
              className="event-poster"
            />

            <div className="event-info">
              <h1>{event.heading}</h1>
              {event.subtitle && <p>{event.subtitle}</p>}
              <p>{event.venue}</p>
              {event.description && <p>{event.description}</p>}

              <div className="badges">
                <span>
                  {dateOnlyFormat(event.start_date)}
                  {event.end_date &&
                    ` - ${dateOnlyFormat(event.end_date)}`}
                </span>
                <span>{event.time}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="container sections-wrapper">
        {event.book_start ? (
          <>
            <h3>Available Sections</h3>

            <div className="sections-container">
              {event.sections?.map((section, i) => {
                const available = getAvailableTickets(section);

                return (
                  <div
                    key={i}
                    className={`section-card ${
                      available <= 0 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      available > 0 && handleSectionClick(section)
                    }
                  >
                    <div>
                      <h5>{section.title}</h5>
                      <p>
                        {available > 0
                          ? `${available} tickets left`
                          : "Sold Out"}
                      </p>
                      {section.group_prices && section.group_prices.length > 0 && (
                        <div className="pricing-tiers">
                          <small>Group Pricing:</small>
                          <table className="tiers-table">
                            <tbody>
                              {section.group_prices
                                .sort((a, b) => a.min_quantity - b.min_quantity)
                                .map((gp, gi) => (
                                  <tr key={gi}>
                                    <td>{gp.min_quantity} tickets</td>
                                    <td>€{parseFloat(gp.group_price).toFixed(2)}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="section-price">
                      €{section.price} <small>(base price)</small>
                    </div>

                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div>
            <h4>Booking not started</h4>
          </div>
        )}
      </div>

      {/* Modal */}
      {showQuantityModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowQuantityModal(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h4>Select Tickets</h4>
            <p>{selectedSection?.title}</p>
            <p>€{selectedSection?.price} / ticket (base)</p>

            <div className="quantity-selector">
              <button
                onClick={() =>
                  setQuantity((prev) => Math.max(1, prev - 1))
                }
              >
                -
              </button>

              <span>{quantity}</span>

              <button
                onClick={() =>
                  quantity < availableTickets &&
                  setQuantity((prev) => prev + 1)
                }
              >
                +
              </button>
            </div>

            {selectedSection && (
              (() => {
                const preview = calcPrice(selectedSection, quantity);
                return (
                  <div className="price-preview">
                    <div className="total-preview">
                      <strong>{quantity} tickets = €{preview.total.toFixed(2)}</strong>
                    </div>
                    {preview.savings > 0 && (
                      <div className="savings-preview">
                        <span>SAVE €{preview.savings.toFixed(2)}!</span>
                      </div>
                    )}
                  </div>
                );
              })()
            )}

            <button
              onClick={handleQuantitySubmit}
              disabled={isProcessing}
            >

              {isProcessing ? "Processing..." : "Proceed"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
